import { getEmojiColorPalette } from "@/components/emoji/emoji-colors.ts";
import { EmployeeItem } from "@/components/employee/EmployeeItem.tsx";
import { GradientContainer } from "@/components/gradient-container/GradientContainer.tsx";
import useFormatter from "@/hooks/use-formatter.hook.ts";
import { usePerformanceReviewRespond } from "@/pages/employees/performance-reviews/performance-review-respond.tsx";
import { useEmployeePerformanceReviewSaveDraft } from "@/queries/employees/use-employee-performance-review.query.ts";
import { useEmployeeMe } from "@/queries/employees/use-employees.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiPerformanceReviewRespondView,
  PerformanceReviewReviewerTypeItems,
} from "@/types/performance-review.types.ts";
import { Avatar, Button, FullScreenPage, ListItem, Stack, Title } from "@skailer-com/uikit";
import { round } from "lodash";
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

export const PerformanceReviewStart = ({ review }: { review: ApiPerformanceReviewRespondView }) => {
  const { t } = useTranslation();
  const { renderEmployeeName } = useFormatter();
  const { exit } = usePerformanceReviewRespond();

  const reviewStart = useEmployeePerformanceReviewSaveDraft();
  const { data: employee } = useEmployeeMe();
  const { renderDate } = useFormatter();

  const handleStartReview = async () => {
    if (!employee) return;
    try {
      await reviewStart.mutateAsync({
        employeeId: employee.id,
        questionnaireId: review.reviewer_id,
        answers: [],
      });

      analyticsService.trackEvent(analyticEvents.performanceReviews.responseStarted, {
        [analyticProperties.id]: review.reviewer_id,
      });
    } catch {
      // Handle error
    }
  };

  const emojiColors = useMemo(
    () => (review.emoji ? getEmojiColorPalette(review.emoji) : undefined),
    [review]
  );

  return (
    <>
      <FullScreenPage
        backgroundSlot={
          emojiColors ? (
            <GradientContainer colorPalette={emojiColors} addLightOverlayTop={true} />
          ) : undefined
        }
        titleSlot={<Title header={review.title} />}
        isCenterVertical={false}
        backSlot={
          <Button variant={"secondary"} size={"lg"} onClick={exit}>
            {t("common:exit")}
          </Button>
        }
      >
        <Stack className={"card-panel p-6 bg-light"} justify={"between"} gap={"xl"}>
          <Avatar emoji={review.emoji} size={"2xl"} variant={"opaque"} className={"shadow-lg"} />
          <Title header={review.title} caption={review.description || undefined} size={"lg"} />
          <Stack>
            <ListItem
              title={t("performance_review:reviewee")}
              valueSlot={<EmployeeItem employee={review.participant} />}
            />
            <ListItem
              title={t("performance_review:review_type")}
              value={
                PerformanceReviewReviewerTypeItems().find((x) => x.id == review.reviewer_type)
                  ?.title ??
                review.reviewer_type_title ??
                ""
              }
            />
            {review.comments &&
              review.comments.map((comment) => (
                <ListItem
                  key={`comment-${comment.id}`}
                  title={t("performance_review:focus_from", {
                    name: renderEmployeeName(comment.author),
                  })}
                  caption={comment.text}
                />
              ))}
            <ListItem
              title={t("common:questions")}
              value={review.form_stats.total_questions.toString()}
            />
            <ListItem
              title={t("common:type")}
              value={review.is_anonymous ? t("common:anonymous") : t("common:non_anonymous")}
            />
            <ListItem
              title={t("common:time_to_complete")}
              value={t("plurals:plural_minutes", {
                count: round(review.form_stats.total_estimated_time_seconds / 60),
              })}
            />
            {review.date_starts_at && review.date_ends_at && (
              <ListItem
                title={t("common:dates")}
                value={`${renderDate(review.date_starts_at)} - ${renderDate(review.date_ends_at)}`}
              />
            )}
          </Stack>
          <Stack>
            <Button onClick={handleStartReview} isLoading={reviewStart.isPending} size={"lg"}>
              {t("performance_review:start_review")}
            </Button>
          </Stack>
        </Stack>
      </FullScreenPage>
    </>
  );
};
